import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { IndustrySelector } from "@/components/detail/IndustrySelector";
import { FaqAccordion } from "@/components/detail/FaqAccordion";
import { DetailCta } from "@/components/detail/DetailCta";
import { industryDetails } from "@/data/industryDetails";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { ArrowUpRight } from "@/icons";
import { siteConfig } from "@/config/site.config";
import MarketingShowcase from "@/components/common/MarketingShowcase";
import IndustryValueSection from "@/components/industries/IndustryValueSection";
import IndustryTransformation from "@/components/industries/IndustryTransformation";

const industriesFaqs = [
  { question: "Do you really understand our industry?", answer: "We pair proven engineering with focused domain discovery. Before we build, we map your specific workflows, terminology, compliance needs and edge cases — so the software fits how your sector actually operates." },
  { question: "We're in an industry not listed here. Can you still help?", answer: "Yes. These are the sectors we most commonly serve, but our approach is domain-agnostic. If your business runs on processes and data, we can design and build for it." },
  { question: "Do you build from scratch or use ready-made templates?", answer: "We start from battle-tested blueprints for your industry, then tailor them to your exact processes. You get the speed of a proven foundation with the fit of a custom build." },
  { question: "Can you integrate with the systems we already use?", answer: "Absolutely. Every industry solution is designed to connect with the tools, data sources and hardware you already rely on." },
  { question: "How do you handle industry-specific compliance and security?", answer: "Compliance is designed in from day one — from data residency and audit trails to role-based access — matched to the regulations your sector operates under." },
];

export default function Industries() {
  return (
    <>
      <Seo
        title="Industries We Serve | AI Solutions for Every Sector | VibeWell"
        description="Discover AI-powered software solutions designed for healthcare, education, manufacturing, retail, logistics, government, professional services and more."
        path="/industries"
      />
      <PageHero
        eyebrow="Industries"
        titleLines={["Industry Expertise", "Combined with", "AI Innovation"]}
        description="Every industry has unique workflows, compliance requirements and operational challenges. VibeWell designs intelligent digital solutions tailored to the way your organisation works."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
            Book a Free Consultation
          </CtaButton>
          <CtaButton to="/solutions" variant="outline">
            Explore Solutions
          </CtaButton>
        </div>
      </PageHero>

      <MarketingShowcase
        eyebrow="Industry Solutions"

        title="Industry-Focused AI Solutions Built Around Your Business"

        description="
          Every industry operates differently. Rather than forcing your business into generic software, we design AI-powered solutions that reflect your workflows, compliance requirements and operational goals."

        bullets={[
            "Industry-specific business processes",
            "Regulatory and compliance awareness",
            "AI-powered workflow automation",
            "Enterprise integrations",
            "Real-time operational intelligence",
        ]}

        image={"/images/seo/industries-og.webp"}

        imageAlt="Industry Solutions"

        accent="#0F8B8D"

        primaryCta={{
            label: "Discuss Your Industry",
            href: "/contact",
        }}

        secondaryCta={{
            label: "Explore Solutions",
            href: "/solutions",
        }}
    />

      {/* Interactive selector */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Explore by sector"
            title="Explore your industry"
            description="Explore how we help organisations in your sector through industry-specific dashboards, workflows and technology recommendations."
          />
          <div className="mt-12">
            <IndustrySelector industries={industryDetails} />
          </div>
        </Container>
      </Section>

      {/* Full grid of industries */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="All industries"
            title="Where we go deep"
            description="Twelve sectors, each with dedicated pain points, workflows and technology recommendations."
          />
          <motion.div
            variants={staggerContainer(0.05, 0.03)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {industryDetails.map((ind) => (
              <motion.div key={ind.slug} variants={fadeUp} data-testid={`industries-card-${ind.slug}`}>
                <Link
                  to={`/industries/${ind.slug}`}
                  className="group flex h-full flex-col rounded-[1.25rem] border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-accent/30 hover:shadow-[0_24px_60px_-28px_rgba(6,182,212,0.35)]"
                >
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white"
                    style={{ background: ind.accent }}
                  >
                    <ind.icon className="h-5 w-5" />
                  </span>
                  <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {ind.sector}
                  </p>
                  <h3 className="mt-1.5 font-heading text-lg font-semibold text-foreground">
                    {ind.name}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {ind.tagline}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">

                    {ind.tags.map((tag) => (
                      <span key={tag} className="rounded-full border bg-muted/40 px-3 py-1 text-xs font-medium">
                          {tag}
                      </span>

                    ))}

                  </div>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-all duration-300 group-hover:gap-2.5">
                    Explore industry <ArrowUpRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      <IndustryTransformation />

      <IndustryValueSection />

      {/* FAQ */}
      <Section spacing="compact">
        <Container>
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" align="center" />
          <div className="mt-10">
            <FaqAccordion faqs={industriesFaqs} />
          </div>
        </Container>
      </Section>

      <DetailCta />
    </>
  );
}
